import React, { useEffect, useState } from 'react';
import { useLanguage } from 'modules/LanguageContext';
import Text, { resolveText } from 'components/Text/Text';

// Stage 3.9 (Enterprise Candidate Experience Final UI Polish & UX Optimization) — Tasks 5 & 6.
// One reusable component, mounted in exactly 2 places (layouts/PublicLayout.jsx and
// layouts/PrivateLayout.jsx — see ERPM_S3_9_Component_Consistency_Report.md), so it reaches every
// public candidate page and every authenticated candidate page without any per-page edit. No API call,
// validation, business logic, workflow, or payment code is touched — this reads only the anonymous,
// non-sensitive Language config (modules/LanguageContext.js) and writes only <html> class names /
// localStorage.
//
// Font scale: cycles html.font-scale-sm/-md/-lg (themes.scss). The app's typography already runs on
// rem/em throughout (Stage 3.2 token system), so this scales proportionally almost everywhere with no
// per-component change.
// High contrast: toggles html.high-contrast, which redefines the existing color tokens under
// :root.high-contrast in themes.scss — every rule that already reads var(--text-color) etc. respects
// it automatically.
// Language: Phase C.3 (Candidate Runtime Language Integration & Legacy Reconciliation) removed the
// candidate-facing language and display-mode <select> controls that used to live here — the Admin's
// PortalLanguage configuration (Phase C.2/C.2.1) is now the sole authority over candidate locale/
// displayMode, and candidates can no longer override it. This component still reads locale/displayMode
// from modules/LanguageContext.js purely to render its OWN remaining labels (accessibility heading,
// font-size button aria-labels) in the admin-configured language — it no longer lets the candidate
// change that language.

const FONT_SCALE_KEY = 'ENT_FONT_SCALE';
const HIGH_CONTRAST_KEY = 'ENT_HIGH_CONTRAST';
const FONT_SCALES = ['sm', 'md', 'lg'];

// Staff (Admin/BankAdmin/HelpDesk/Biometric) pre-login screens share PublicLayout with candidate public
// pages (see ERPM_S3_9_Component_Consistency_Report.md, Finding 5) — this keeps the toolbar
// candidate-only there without touching PublicLayout.jsx's own export/wrapping.
const STAFF_PATH_PREFIXES = ['/admin', '/bankadmin', '/helpdeskadmin', '/biometricadmin'];

function isStaffPath(pathname) {
  const lower = (pathname || '').toLowerCase();
  return STAFF_PATH_PREFIXES.some(prefix => lower.startsWith(prefix));
}

// Sprint 2.6.7B (Candidate Homepage — Port-85 Baseline Restoration, §7/§8): the port-85 baseline has
// no accessibility/language bar at all on the homepage, not even a compact one (2.6.7A's collapsed
// toggle was reconsidered and rejected — see 2.6.7B report). The homepage now renders nothing here.
// This does NOT remove the underlying font-scale/high-contrast/language capability — it still fully
// exists and renders exactly as before on every other public page (Registration, Forgot/Reset/Set
// Password) and every authenticated page (PrivateLayout); only the homepage's own presentation of it
// is suppressed, mirroring the existing isStaffPath() pattern just below.
function isHomePath(pathname) {
  return pathname === '/';
}

// Phase C.7.4B (§3): Candidate Registration Form Visual Hierarchy/Typography/Shell Refinement asks
// for the visible toolbar band removed from Registration specifically — NOT for the underlying
// font-scale/high-contrast capability to be removed (it still fully exists and still applies its
// classes to <html>, still works via browser zoom/native controls; this only stops this one page
// from rendering its own copy of the control band). Mirrors the exact isHomePath()/isStaffPath()
// self-suppression pattern already established here, just for one more route.
// Phase C.7.4C (§11.1): the Registration Success page (`/registrationComplete`) joins the same
// suppression — it's part of the same "Form 1 family" journey as Registration itself.
const REGISTRATION_JOURNEY_PATHS = ['/registration', '/registrationComplete'];
function isRegistrationPath(pathname) {
  return REGISTRATION_JOURNEY_PATHS.includes(pathname);
}

// Phase C.7.4C (§16 Logged-In Candidate Landing Page): the authenticated candidate area
// (CandidateRegistration.jsx's single `/home` route, covering the dashboard/summary view and every
// wizard tab — there is no separate URL per tab — plus the post-payment `/transactionprocess`
// interstitial) is now part of the same "one coherent candidate journey" this whole C.7.4 series has
// been closing. Same self-suppression pattern, same underlying font-scale/high-contrast mechanism
// left fully intact (still reachable/settable from any public page beforehand, still applied here).
const PRIVATE_PATHS = ['/home', '/transactionprocess'];
function isPrivatePath(pathname) {
  return PRIVATE_PATHS.includes(pathname);
}

// Phase C.7.4D-R1 (§2/§11 Forgot Password Candidate Portal Theme Migration): Forgot Password is
// reached directly from the Login modal's "Forgot Password?" link as part of the same
// pre-authentication candidate journey Registration/Registration Success already suppress this
// toolbar band on (§ "Forgot Password still uses the legacy layout... Accessibility Options
// toolbar" was named explicitly as one of several legacy-shell artifacts on this page). Same
// self-suppression pattern, one more route; the underlying font-scale/high-contrast mechanism is
// untouched and still fully reachable/settable from any other public page beforehand. Reset/Set
// Password intentionally NOT included — they still render the older `.login-form-wrap` shell in
// full (not just this toolbar), out of this phase's named scope; disclosed as a known follow-up.
function isForgotPasswordPath(pathname) {
  return pathname === '/forgot-password';
}

function applyFontScale(scale) {
  document.documentElement.classList.remove('font-scale-sm', 'font-scale-md', 'font-scale-lg');
  document.documentElement.classList.add('font-scale-' + scale);
}

function applyHighContrast(enabled) {
  document.documentElement.classList.toggle('high-contrast', enabled);
}

export default function AccessibilityToolbar() {
  // Phase C.3: enabledLanguages/setLocale/setDisplayMode are no longer used here — the candidate-facing
  // language/display-mode selector controls were removed (Decision 2.1).
  const { locale, displayMode } = useLanguage();

  const [fontScale, setFontScale] = useState(() => {
    try { return localStorage.getItem(FONT_SCALE_KEY) || 'md'; } catch (e) { return 'md'; }
  });
  const [highContrast, setHighContrast] = useState(() => {
    try { return localStorage.getItem(HIGH_CONTRAST_KEY) === 'true'; } catch (e) { return false; }
  });

  useEffect(() => { applyFontScale(fontScale); }, [fontScale]);
  useEffect(() => { applyHighContrast(highContrast); }, [highContrast]);

  if (typeof window !== 'undefined' &&
    (isStaffPath(window.location.pathname) || isHomePath(window.location.pathname) ||
      isRegistrationPath(window.location.pathname) || isPrivatePath(window.location.pathname) ||
      isForgotPasswordPath(window.location.pathname))) {
    return null;
  }

  function changeFontScale(direction) {
    const currentIndex = FONT_SCALES.indexOf(fontScale);
    const nextIndex = Math.min(FONT_SCALES.length - 1, Math.max(0, currentIndex + direction));
    const next = FONT_SCALES[nextIndex];
    setFontScale(next);
    try { localStorage.setItem(FONT_SCALE_KEY, next); } catch (e) { /* non-fatal */ }
  }

  function toggleHighContrast() {
    const next = !highContrast;
    setHighContrast(next);
    try { localStorage.setItem(HIGH_CONTRAST_KEY, String(next)); } catch (e) { /* non-fatal */ }
  }

  const fontSizeControls = (
    <span className="ent-accessibility-toolbar-group" aria-label="Font size">
      <button type="button" className="ent-accessibility-toolbar-btn" onClick={() => changeFontScale(-1)}
        aria-label={resolveText('accessibilityToolbar.decreaseFontSize', locale, displayMode)} title="A-">
        A-
      </button>
      <button type="button" className="ent-accessibility-toolbar-btn" onClick={() => changeFontScale(0)}
        aria-label={resolveText('accessibilityToolbar.resetFontSize', locale, displayMode)} title="A">
        A
      </button>
      <button type="button" className="ent-accessibility-toolbar-btn" onClick={() => changeFontScale(1)}
        aria-label={resolveText('accessibilityToolbar.increaseFontSize', locale, displayMode)} title="A+">
        A+
      </button>
    </span>
  );

  const highContrastControl = (
    <button type="button"
      className={"ent-accessibility-toolbar-btn" + (highContrast ? " ent-accessibility-toolbar-btn-active" : "")}
      onClick={toggleHighContrast} aria-pressed={highContrast}>
      <Text k="accessibilityToolbar.highContrast" />
    </button>
  );

  // Phase C.3 Decision 2.1: the language <select> and display-mode <select> controls that used to live
  // here were removed — candidates can no longer choose or override the Admin-configured portal
  // language. Font-size and high-contrast (unrelated accessibility features) are fully preserved above.

  return (
    <div className="ent-accessibility-toolbar" role="region" aria-label="Accessibility options">
      <span className="ent-accessibility-toolbar-label">
        <Text k="accessibilityToolbar.accessibilityOptions" />
      </span>

      {fontSizeControls}
      {highContrastControl}
    </div>
  );
}
